import React from 'react';
import PropTypes from 'prop-types';
import { getBulkDiscount } from '@repo/lib';

import { formatPrice } from '../../services/formatPrice';

import getSubscriptionIntervalWithSlash from '../../services/translation/getSubscriptionIntervalWithSlash';
import MultilineText from '../../components/MultilineText';
import { descriptionClassName } from '../../lib/descriptionClassName';
import { descriptionWhiteSpace } from '../../lib/descriptionWhiteSpace';
import clsx from 'clsx';
import Badge from '../../components/Badge';
import ProductPricePicker from './ProductPricePicker';
import ProductPwywField from './ProductPwywField';
import ProductPwywSubscriptionTerms from './ProductPwywSubscriptionTerms';
import ModulesEnum, {
  getEventModules,
  getFormModules,
  getProductModules,
} from '../../enums/modules';
import sanitizeHtml from 'sanitize-html';
import { formatInTimeZone } from 'date-fns-tz';
import checkHasValidStartDate from '../../lib/checkHasValidStartDate';
import formatStartDate from '../../lib/formatStartDate';
import { getImageSrcAndSrcSet, stripQueryStringFromCdnUrl } from '../../services/getUrlFromCdn';
import useCartCost from '../../hooks/useCartCost';
import useEffectivePricing from '../../hooks/useEffectivePricing';
import useCheckoutState, { checkoutLayouts } from '../../hooks/useCheckoutState';
import useAiTarget from '../../hooks/useAiTarget';
import {
  isMultiPrice,
  selectablePrices,
  isSinglePwyw as checkIsSinglePwyw,
  isRecurringPurchase,
} from '../../lib/multiPrice';
import getPriceBillingConfig from '../../lib/getPriceBillingConfig';
import { queryDiscountedFrom } from '../../lib/discountedFrom';
import useSelectedPrice from '../../hooks/useSelectedPrice';
import useSetupFee from '../../hooks/useSetupFee';
import * as Avatar from '@radix-ui/react-avatar';
import { PAGE_TYPES } from '../../enums/page-types';
import { getMedia } from '../../lib/getMedia';
import getPlanIterations from '../../lib/getPlanIterations';
import { splitBulkDiscounts } from '../../lib/splitBulkDiscounts';
import useQuantity from '../../hooks/useQuantity';
import useProration from '../../hooks/useProration';
import { getDescription } from '../../lib/getDescription';
import StyledElement from '../StyledElement';
import TestModeBanner from '../TestModeBanner';
import { useMemo } from 'react';
import { checkoutTypes } from '@repo/checkout/enums/checkout-types';
import getThemeLabels from '@repo/checkout/lib/getThemeLabels';
import Media from '../Media';

// Kept literal so Tailwind's JIT emits the arbitrary-value classes.
const productPriceIntervalClasses = [
  'cp_product-price-interval',
  '[color:var(--cp_product-price-interval-color)]',
  '[font-family:var(--cp_product-price-interval-font-family)]',
  '[font-style:var(--cp_product-price-interval-font-style)]',
  '[font-size:var(--cp_product-price-interval-font-size)]',
  '[line-height:var(--cp_product-price-interval-line-height)]',
  '[font-weight:var(--cp_product-price-interval-font-weight)]',
];

function getTitle({ allowDynamicTitle, dynamicTitle, title }) {
  if (!allowDynamicTitle) return title;

  if (dynamicTitle) {
    return sanitizeHtml(dynamicTitle.replace(/\\n/g, '\n'));
  }

  return title;
}

const propTypes = {
  checkout: PropTypes.object.isRequired,
  seller: PropTypes.object.isRequired,
};

const Product = ({ flushMediaBottom = false }) => {
  const { theme, isScreenshot, checkoutLayout, checkout, seller, query, isPreview, checkoutType } =
    useCheckoutState();
  const aiTarget = useAiTarget();
  const { productPriceAmount, setupFeeAmount } = useCartCost();
  const { effectiveDiscountedFromPrice, effectiveTrialPeriodDays, effectiveStartDate } =
    useEffectivePricing();

  const { product } = checkout;

  const modules = useMemo(() => {
    let modules;
    if (checkout.type === PAGE_TYPES.CHECKOUT) {
      modules = getProductModules({
        theme,
        checkout,
        seller,
        query,
        isScreenshot,
        productPriceAmount,
        setupFeeAmount,
        discountedFromPrice: effectiveDiscountedFromPrice,
        trialPeriodDays: effectiveTrialPeriodDays,
        startDate: effectiveStartDate,
        isPreview,
        checkoutType,
        checkoutLayout,
      });
    } else if (checkout.type === PAGE_TYPES.FORM) {
      modules = getFormModules({
        theme,
        checkout,
        seller,
        isScreenshot,
        query,
        isPreview,
        checkoutType,
      });
    } else if (checkout.type === PAGE_TYPES.EVENT) {
      modules = getEventModules({
        theme,
        checkout,
        seller,
        isScreenshot,
        query,
        isPreview,
        checkoutType,
      });
    }
    return modules;
  }, [
    theme,
    checkout,
    seller,
    query,
    isScreenshot,
    isPreview,
    productPriceAmount,
    setupFeeAmount,
    effectiveDiscountedFromPrice,
    effectiveTrialPeriodDays,
    effectiveStartDate,
    product,
    checkoutType,
    checkoutLayout,
  ]);

  const labels = getThemeLabels(theme);

  // When the multi-price PricePicker is shown it IS the price UI, so the
  // standalone product price would render redundantly above the grouping
  // toggles. Suppress it in that case.
  // Hidden prices still count toward "multi-price" (they're revealable via a query
  // param), so a product with a hidden sibling keeps the picker — showing its
  // visible option(s); the hidden price appears once revealed.
  const showPricePicker = isMultiPrice(product?.prices, query);
  // The PRICE_PICKER module already accounts for the element's own toggle and
  // its 'form' position — see getProductModules.
  const pickerInContent = Boolean(modules[ModulesEnum.PRICE_PICKER]);

  // Single-price products don't render the PricePicker, so surface the lone
  // visible price's badge here instead (reusing the picker's badge styling).
  const onlyPrice = !showPricePicker ? selectablePrices(product?.prices, query)[0] : null;
  const singlePriceBadge = onlyPrice?.badge ?? null;
  // A single pay-what-you-want price collects its amount via the input rather
  // than showing the minimum as a fixed-looking price line under the title.
  const isSinglePwyw = checkIsSinglePwyw(product, query);
  // Where the single-price PWYW input renders — content-top is the default and
  // matches the historical (always-content) behavior; ProductPwywField itself
  // self-gates on this same read, so this only controls whether THIS site's
  // wrapper (badge + spacing) shows.
  const pwywPosition = theme?.elements?.checkout?.pwyw?.position || 'content-top';
  // The single PWYW block under the title has content when the amount input
  // renders there (content-top). The highlight badge renders above the title
  // (not here), so it no longer factors in. Also gates the SetupFee fallback
  // spacing so only one of them owns the mt-4.
  const showSinglePwywContentBlock =
    isSinglePwyw && modules[ModulesEnum.PRICE] && pwywPosition === 'content-top';

  const siteUrl = seller.url;

  const ImageWrapper = siteUrl ? 'a' : 'div';

  const isSoldOut = product && !product.hasUnlimitedStock && product.stock === 0;

  const title = getTitle({
    allowDynamicTitle: checkout.allowDynamicTitle,
    title: product?.title || checkout.title,
    dynamicTitle: query.title,
  });

  const description = (() => {
    const description = getDescription({
      allowDynamicDescription: checkout.allowDynamicDescription,
      dynamicDescription: query.description,
      productDescription: product?.description,
      productDescriptionHtml: product?.descriptionHtml,
      checkoutDescription: checkout.description,
      checkoutDescriptionHtml: checkout.descriptionHtml,
    });

    if (isPreview) {
      return stripQueryStringFromCdnUrl(description);
    }

    return description;
  })();

  const media = getMedia({ checkout, product });
  const hasMedia = media && media.length > 0;

  // Layout type checks
  const isEmbed = checkoutType === checkoutTypes.EMBED;
  const isPopup = checkoutType === checkoutTypes.POPUP;
  const isCheckout = checkoutType === checkoutTypes.CHECKOUT;

  const hasLogoBubble =
    modules[ModulesEnum.LOGO] &&
    (isEmbed ||
      isPopup ||
      (isCheckout && theme.elements?.product?.logo?.hostedPageLogoStyle === 'logoBubble'));

  const logoSizePx = Number(theme?.elements?.product?.logo?.logoBubble?.styles?.size) || 80;

  const isDoubleLayout = checkoutLayout === checkoutLayouts.DOUBLE;
  const isSplitLayout = checkoutLayout === checkoutLayouts.SPLIT;
  const isCenterLayout = !isDoubleLayout && !isSplitLayout;

  // Logo positioning: use relative positioning when there's no media to position against
  const shouldUseRelativePositioning =
    (isEmbed && !hasMedia) || (isCheckout && (isDoubleLayout || isSplitLayout) && !hasMedia);

  // Mirrors checkHasProductWrapperContent in enums/modules.js — keep the two in sync.
  const hasProductWrapperContent =
    hasLogoBubble ||
    modules[ModulesEnum.TITLE] ||
    modules[ModulesEnum.PRICE] ||
    pickerInContent ||
    modules[ModulesEnum.SETUP_FEE] ||
    modules[ModulesEnum.DESCRIPTION];

  return (
    <div>
      {hasMedia && (
        <div
          {...aiTarget('images')}
          className={clsx('cp_product-image-wrapper', {
            'mb-4':
              (isDoubleLayout || isSplitLayout || isCenterLayout) && !hasLogoBubble && !hasMedia,
          })}
          style={{
            ...(isCenterLayout
              ? {
                  marginTop: 'calc(-1 * var(--cp_checkout-padding, 0px))',
                  marginLeft: 'calc(-1rem - var(--cp_checkout-padding, 0px))',
                  marginRight: 'calc(-1rem - var(--cp_checkout-padding, 0px))',
                  // When media is the only thing in the content section, extend
                  // the image down to the divider by cancelling the parent's pb-4.
                  // Only flush when nothing (variants) sits between the image
                  // and the divider — caller decides via flushMediaBottom.
                  ...(flushMediaBottom && !hasProductWrapperContent && { marginBottom: '-1rem' }),
                }
              : undefined),
          }}
        >
          <Media media={media} logoClearancePx={hasLogoBubble ? logoSizePx : undefined} />
        </div>
      )}

      {hasProductWrapperContent && (
        <StyledElement
          elementPath="product"
          className={clsx(
            'cp_product-wrapper',
            '[text-align:var(--cp_product-text-align)]',
            'text-[var(--cp_product-color)]',
            'flex flex-col w-full relative',
            {
              // Double layout and center layout with media needs spacing between product wrapper and media
              'mt-4': !hasLogoBubble && hasMedia,
            },
          )}
          /*style={{
          // Checkout without media needs top margin to account for absolute positioned logo
          ...(hasLogoBubble &&
            isCheckout &&
            isCenterLayout &&
            !hasMedia &&
            {
              //marginTop: `calc(${logoSizePx / 4}px - var(--cp_checkout-padding))`,
            }),
        }}*/
        >
          {hasLogoBubble && (
            <>
              <div
                className={clsx(
                  'cp_product-logo-wrapper',
                  'leading-0',
                  '[text-align:var(--cp_product-logo-bubble-text-align)]',
                  {
                    // Absolute positioning centers logo at top of product section; the
                    // full-width strip must not swallow clicks on the attribution under it.
                    'absolute left-0 right-0 pointer-events-none': !shouldUseRelativePositioning,
                    // Embed with relative positioning needs margin adjustment
                    'mt-4': shouldUseRelativePositioning && isEmbed,
                  },
                )}
                style={{
                  // Position logo half its height above the product section top
                  ...(!shouldUseRelativePositioning &&
                    hasMedia && {
                      top: `calc(-1 * (${logoSizePx / 2}px))`,
                    }),
                  ...(!shouldUseRelativePositioning &&
                    !hasMedia && {
                      top: `calc(-1 * (${logoSizePx / 2}px + var(--cp_checkout-padding)))`,
                    }),
                }}
              >
                <ImageWrapper
                  className={clsx(
                    'cp_product-logo',
                    'pointer-events-auto inline-block rounded-full overflow-hidden shadow-sm text-5xl',
                    'bg-[var(--cp_product-logo-bubble-background-color)]',
                    {
                      'hover:shadow': !!siteUrl,
                    },
                  )}
                  rel="nofollow noreferrer"
                  href={siteUrl}
                  target="_blank"
                  style={{
                    width: `${logoSizePx}px`,
                    height: `${logoSizePx}px`,
                  }}
                >
                  <Logo width={logoSizePx} height={logoSizePx} isLogoBubble />
                </ImageWrapper>
              </div>

              {/* spacer div to avoid overlap, 50% of logo size */}
              {!shouldUseRelativePositioning && (
                <div
                  style={{
                    marginBottom: hasMedia
                      ? `max(0px, calc(${logoSizePx / 2}px))`
                      : `max(0px, calc(${logoSizePx / 2}px - var(--cp_checkout-padding))`,
                  }}
                />
              )}
            </>
          )}

          {/* When the picker is showing it IS the price UI, so every price-related
              child below is gated on !showPricePicker — the Price module alone
              would mount this block empty (and its mb-4 gap) the moment a price
              is selected. Ignore it while the picker owns the pricing. */}
          {(modules[ModulesEnum.TITLE] ||
            (modules[ModulesEnum.PRICE] && !showPricePicker) ||
            modules[ModulesEnum.SETUP_FEE]) && (
            <div
              className={clsx(`cp_product-content`, {
                'mt-4': isCenterLayout && !hasLogoBubble && !hasMedia,
                'mb-4': modules[ModulesEnum.DESCRIPTION] || pickerInContent,
              })}
            >
              <TestModeBanner placement="title" />

              <div className="cp_product-title-wrapper justify-between items-start">
                {modules[ModulesEnum.PRICE] && !showPricePicker && singlePriceBadge && (
                  <div>
                    {/* An inline-block sized from max-content, in a block parent that
                        never clips: without these a long highlight spills out of the
                        product column and lands on top of the form beside it. */}
                    <StyledElement
                      as="span"
                      elementPath="product.pricing.badge"
                      className="cp_product-price-badge mb-1 max-w-full whitespace-normal break-words"
                    >
                      {singlePriceBadge}
                    </StyledElement>
                  </div>
                )}

                {modules[ModulesEnum.TITLE] && (
                  <div className="cp_product-title-container" {...aiTarget('title')}>
                    <StyledElement
                      elementPath="product.title"
                      as="h1"
                      className={clsx(
                        'cp_product-title',
                        'break-words',
                        'bg-[var(--cp_product-title-background-color)]',
                        'text-[var(--cp_product-title-color)]',
                        '[font-family:var(--cp_product-title-font-family)]',
                        '[font-style:var(--cp_product-title-font-style)]',
                        '[font-size:var(--cp_product-title-font-size)]',
                        '[line-height:var(--cp_product-title-line-height)]',
                        '[font-weight:var(--cp_product-title-font-weight)]',
                      )}
                    >
                      {title}
                    </StyledElement>
                  </div>
                )}

                <div className="cp_product-price-wrapper" {...aiTarget('price')}>
                  {modules[ModulesEnum.PRICE] && !showPricePicker && !isSinglePwyw && (
                    // mt-2 matches the single PWYW spacing (ProductPwywField), so a
                    // fixed price and a PWYW price sit the same distance below the title.
                    <div>
                      <Price product={product} amount={productPriceAmount} checkout={checkout} />
                    </div>
                  )}

                  {/* A single pay-what-you-want price has no picker — its amount
                      input IS the price. The amount input follows pwywPosition
                      (this site owns the default 'content-top'; other positions
                      mount the field elsewhere via ProductPwywField's self-gate).
                      Gated on the Price module so it hides when the Price element
                      is hidden. The Highlight badge renders above the title. */}
                  {showSinglePwywContentBlock && (
                    <div className="cp_product-pwyw-block mt-4">
                      {pwywPosition === 'content-top' && (
                        <ProductPwywField position="content-top" />
                      )}
                    </div>
                  )}

                  {/* A single PWYW SUBSCRIPTION shows its billing terms (trial,
                      start date, proration) below the amount input — the input's
                      "/month" suffix carries the interval, but <Price> (which
                      owns those lines for a fixed subscription) isn't mounted for
                      a PWYW price. Self-gates to recurring single PWYW; renders
                      null otherwise, so it's safe to mount unconditionally. */}
                  {isSinglePwyw && <ProductPwywSubscriptionTerms className="mt-1" />}

                  {modules[ModulesEnum.SETUP_FEE] && !showPricePicker && (
                    <SetupFee
                      product={product}
                      // Single PWYW with nothing rendered above (input moved
                      // elsewhere and no badge, or the Price element hidden)
                      // leaves the fee as the first content under the title —
                      // keep the same gap so the title spacing stays constant.
                      className={clsx({
                        'mt-4': isSinglePwyw && !showSinglePwywContentBlock,
                      })}
                    />
                  )}
                </div>
              </div>

              {isSoldOut ? (
                <Badge
                  className={clsx(
                    'cp_sold-out cp_product-sold-out',
                    'text-[var(--cp_checkout-statusBadges-soldOut-color)]',
                    'bg-[var(--cp_checkout-statusBadges-soldOut-background-color)]',
                    '[font-family:var(--cp_checkout-statusBadges-font-family)]',
                    '[font-style:var(--cp_checkout-statusBadges-font-style)]',
                    '[font-size:var(--cp_checkout-statusBadges-font-size)]',
                    '[line-height:var(--cp_checkout-statusBadges-line-height)]',
                    '[font-weight:var(--cp_checkout-statusBadges-font-weight)]',
                  )}
                >
                  {labels.soldOutText}
                </Badge>
              ) : (
                ''
              )}
            </div>
          )}

          {pickerInContent && (
            <ProductPricePicker
              className={clsx({
                'mb-4': modules[ModulesEnum.DESCRIPTION],
                'mt-4': !modules[ModulesEnum.TITLE] && isCenterLayout,
              })}
            />
          )}

          {modules[ModulesEnum.DESCRIPTION] && (
            <StyledElement
              elementPath="description"
              {...aiTarget('description')}
              className={clsx(
                'cp_product-description-wrapper',
                'flow-root overflow-hidden supports-[overflow:clip]:overflow-x-clip supports-[overflow:clip]:overflow-y-visible',
              )}
              style={{
                ...(isCenterLayout &&
                  !modules[ModulesEnum.MEDIA] &&
                  !modules[ModulesEnum.TITLE] &&
                  !modules[ModulesEnum.PRICE_PICKER] &&
                  !modules[ModulesEnum.PRICE] &&
                  !modules[ModulesEnum.SETUP_FEE] && {
                    marginTop: `max(0px, calc(16px - var(--cp_checkout-padding)))`,
                  }),
              }}
            >
              <div className={descriptionClassName(theme?.elements?.product?.styles?.textAlign)}>
                <MultilineText
                  whiteSpace={descriptionWhiteSpace(description)}
                  dangerouslySetInnerHTML={{
                    __html: description,
                  }}
                />
              </div>
            </StyledElement>
          )}

          {/* content-bottom is an alternate position for the single-price PWYW
              input (see pwywPosition above); ProductPwywField self-gates so this
              is a no-op unless the theme picks this site. mt-4 matches the
              content-top gap so switching position keeps the same rhythm. */}
          <ProductPwywField position="content-bottom" className="mt-4" />
        </StyledElement>
      )}
    </div>
  );
};

Product.propTypes = propTypes;

export default Product;

export function LogoBanner({ className }) {
  const { seller, theme, checkoutType } = useCheckoutState();

  const siteUrl = seller.url;

  const isCheckout = checkoutType === checkoutTypes.CHECKOUT;

  const showLogoBanner =
    isCheckout &&
    theme.elements?.product?.logo?.enabled !== false &&
    theme.elements?.product?.logo?.hostedPageLogoStyle !== 'logoBubble';

  if (!showLogoBanner) {
    return null;
  }

  const bannerStyle = theme?.elements?.product?.logo?.logoBanner?.bannerStyle || 'full';

  return (
    <div className={clsx('absolute top-6 left-4 z-20 mb-4', className)}>
      <div className="cp_sold-by-wrapper">
        {siteUrl ? (
          <a
            href={siteUrl}
            target="_blank"
            rel="nofollow noreferrer"
            className={clsx('flex gap-x-2 items-center mt-px')}
          >
            {(bannerStyle === 'full' || bannerStyle === 'icon') && (
              <div className="cp_sold-by-logo @xl/checkout:shrink-0 rounded-full overflow-hidden">
                <Logo
                  width={theme?.elements?.product?.logo?.logoBanner?.styles?.size || 24}
                  height={theme?.elements?.product?.logo?.logoBanner?.styles?.size || 24}
                  isLogoBanner
                />
              </div>
            )}

            {(bannerStyle === 'full' || bannerStyle === 'name') && (
              <div
                title={seller.displayName}
                className={clsx(
                  'cp_sold-by-seller',
                  '@xl/checkout:truncate',
                  'font-medium',
                  '[font-family:var(--cp_product-logo-banner-font-family)]',
                  '[font-style:var(--cp_product-logo-banner-font-style)]',
                  '[font-size:var(--cp_product-logo-banner-font-size)]',
                  '[line-height:var(--cp_product-logo-banner-line-height)]',
                  '[font-weight:var(--cp_product-logo-banner-font-weight)]',
                  'text-[var(--cp_product-logo-banner-color)]',
                )}
              >
                {seller.displayName}
              </div>
            )}
          </a>
        ) : (
          <div className={clsx('flex gap-x-2 items-center mb-6 mt-px')}>
            {(bannerStyle === 'full' || bannerStyle === 'name') && (
              <div className="cp_sold-by-logo @xl/checkout:shrink-0 rounded-full overflow-hidden">
                <Logo
                  width={theme?.elements?.product?.logo?.logoBanner?.styles?.size || 24}
                  height={theme?.elements?.product?.logo?.logoBanner?.styles?.size || 24}
                  isLogoBanner
                />
              </div>
            )}

            {(bannerStyle === 'full' || bannerStyle === 'name') && (
              <div
                title={seller.displayName}
                className={clsx(
                  'cp_sold-by-seller',
                  '@xl/checkout:truncate',
                  'font-medium',
                  'text-[var(--cp_product-logo-banner-color)]',
                  '[font-family:var(--cp_product-logo-banner-font-family)]',
                  '[font-style:var(--cp_product-logo-banner-font-style)]',
                  '[font-size:var(--cp_product-logo-banner-font-size)]',
                  '[line-height:var(--cp_product-logo-banner-line-height)]',
                  '[font-weight:var(--cp_product-logo-banner-font-weight)]',
                )}
              >
                {seller.displayName}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export function PriceWrapper({ children }) {
  return <div className="cp_product-price-container gap-x-2">{children}</div>;
}

export function SetupFee({ product, className }) {
  const { currencyLocale, theme } = useCheckoutState();
  const labels = getThemeLabels(theme);
  // Resolve from the selected price (multi-price) so the displayed fee matches
  // what's charged; legacy products fall back to the product-level fee.
  const { setupFee } = useSetupFee();

  if (!setupFee) return '';

  return (
    <div className={clsx('cp_setup-fee-wrapper', className)}>
      <StyledElement
        elementPath="product.pricing.setupFee"
        as="span"
        className={clsx(
          'cp_setup-fee',
          'bg-[var(--cp_product-price-setupFee-background-color)]',
          'text-[var(--cp_product-price-setupFee-color)]',
          '[font-family:var(--cp_product-price-setupFee-font-family)]',
          '[font-style:var(--cp_product-price-setupFee-font-style)]',
          '[font-size:var(--cp_product-price-setupFee-font-size)]',
          '[font-weight:var(--cp_product-price-setupFee-font-weight)]',
          '[line-height:var(--cp_product-price-setupFee-line-height)]',
        )}
      >
        <span className="cp_setup-fee-amount">
          {formatPrice({
            amount: setupFee,
            // TODO(M14): legacy product.currency — repoint to selected price before legacy fields are removed
            currency: product.currency,
            locale: currencyLocale,
            style: 'currency',
          })}{' '}
        </span>
        <span className="cp_setup-fee-text">{labels.setupFeeText}</span>
      </StyledElement>
    </div>
  );
}

export function DiscountedFromPrice({ product, checkout, priceBeforeBulkDiscount }) {
  const { query, currencyLocale } = useCheckoutState();
  const selectedPrice = useSelectedPrice();

  // The legacy unprefixed `discounted_from` carries no reference, so it is
  // single-price only — on a multi-price product it would restrike every option.
  const dynamicDiscountedFromPrice = queryDiscountedFrom(selectedPrice?.reference, query, {
    allowLegacyFallback: !isMultiPrice(product?.prices, query),
  });

  // Multi-price products keep the discount on the selected price subdoc (the
  // product-level field is only set for legacy single-price products), so resolve
  // from the selected price whenever the product has a prices[] array.
  // TODO(M14): legacy product-scalar fallback — remove once the prices[] backfill migration has run (product.discountedFromPrice)
  const baseDiscountedFromPrice =
    product.prices?.length > 0 ? selectedPrice?.discountedFromPrice : product.discountedFromPrice;

  let discountedFromPrice =
    checkout.allowDynamicDiscountedFromPrice && dynamicDiscountedFromPrice >= 0
      ? dynamicDiscountedFromPrice
      : baseDiscountedFromPrice;

  // Use the priceBeforeBulkDiscount if provided and there's no existing discounted from price
  if (!discountedFromPrice && priceBeforeBulkDiscount) {
    discountedFromPrice = priceBeforeBulkDiscount;
  }

  if (!discountedFromPrice) return '';

  // TODO(M14): legacy product-scalar fallback — remove once the prices[] backfill migration has run (product.payWhatYouWant)
  if (selectedPrice ? selectedPrice.payWhatYouWant : product.payWhatYouWant) return '';

  return (
    <StyledElement
      elementPath="product.pricing.discountedFromPrice"
      as="span"
      className={clsx(
        'cp_product-discounted-from-price',
        'line-through mr-2',
        'bg-[var(--cp_product-price-discountedFromPrice-background-color)]',
        'text-[var(--cp_product-price-discountedFromPrice-color)]',
        '[font-family:var(--cp_product-price-discountedFromPrice-font-family)]',
        '[font-style:var(--cp_product-price-discountedFromPrice-font-style)]',
        '[font-size:var(--cp_product-price-discountedFromPrice-font-size)]',
        '[font-weight:var(--cp_product-price-discountedFromPrice-font-weight)]',
        '[line-height:var(--cp_product-price-discountedFromPrice-line-height)]',
      )}
    >
      {formatPrice({
        amount: discountedFromPrice,
        // TODO(M14): legacy product.currency — repoint to selected price before legacy fields are removed
        currency: product.currency,
        locale: currencyLocale,
        style: 'currency',
      })}
    </StyledElement>
  );
}

export function Price({ product, amount, checkout }) {
  const { dynamicPlanIterations, query, currencyLocale, theme, selectedPriceId } =
    useCheckoutState();
  const selectedPrice = useSelectedPrice();
  const quantity = useQuantity();
  const { proration, hasProration, isLoadingProration } = useProration();
  const labels = getThemeLabels(theme);

  // For multi-price products, resolve the selected price's interval so the
  // display shows e.g. "$10/mo" when the customer picks a recurring price
  // (even if the product's root-level billingType differs).
  // Returns null when no interval should be shown (one-time price selected).
  const effectiveInterval = useMemo(() => {
    // Resolve from prices[] whenever the product has one — including
    // single-price products (where there's no picker and no selectedPriceId).
    // Resolution order: selectedPriceId → defaultPriceId → first enabled price.
    const prices = product?.prices;
    if (prices && prices.length > 0) {
      const enabledPrices = selectablePrices(prices, query);
      const selectedPrice =
        (selectedPriceId && enabledPrices.find(p => String(p._id) === String(selectedPriceId))) ||
        (product.defaultPriceId &&
          enabledPrices.find(p => String(p._id) === String(product.defaultPriceId))) ||
        enabledPrices[0];
      const config = getPriceBillingConfig(selectedPrice);
      if (selectedPrice?.billingType === 'recurring' && config?.interval) {
        return { interval: config.interval, intervalCount: config.intervalCount };
      }
      // Selected/only price is one-time — no interval suffix
      return null;
    }
    // Legacy product without a prices[] array — fall back to root fields.
    // TODO(M14): legacy product-scalar fallback — remove once the prices[] backfill migration has run (product.interval/intervalCount)
    if (!product.interval) return null;
    return { interval: product.interval, intervalCount: product.intervalCount };
  }, [product, selectedPriceId]);

  // Multi-price products carry the start date on the selected price, not the
  // root product — resolve it like effectiveInterval/discountedFromPrice so the
  // "Starts <date>" badge reflects the chosen price (legacy single-price keeps
  // product.startDate).
  // TODO(M14): legacy product-scalar fallback — remove once the prices[] backfill migration has run (product.startDate)
  const effectiveStartDate =
    product?.prices?.length > 0
      ? getPriceBillingConfig(selectedPrice)?.startDate
      : product.startDate;

  // Trial period also lives on the selected price for multi-price products
  // (legacy single-price keeps product.trialPeriodDays). A trial only applies to a
  // recurring price, so gate on effectiveInterval — matching the multi-price
  // PricePicker (isTrial) and the subscription branch below. The charge branch
  // below renders the trial itself, since a charge product can carry a recurring
  // (payment-plan / trial) price in the multi-price model.
  // TODO(M14): legacy product-scalar fallback — remove once the prices[] backfill migration has run (product.trialPeriodDays)
  const effectiveTrialPeriodDays =
    product?.prices?.length > 0
      ? getPriceBillingConfig(selectedPrice)?.trialPeriodDays
      : product.trialPeriodDays;
  const hasTrial = !!effectiveInterval && effectiveTrialPeriodDays > 0;

  // Split bulk discounts and get only the subtotal discounts
  const { subtotalDiscounts } = splitBulkDiscounts(product.discounts);

  // Calculate bulk discount
  const { bulkDiscountAmount } = getBulkDiscount(subtotalDiscounts, quantity, amount * quantity);

  const discountedPrice = amount - bulkDiscountAmount / quantity;

  // Per-price for multi-price products; legacy single-price (no prices[]) keeps
  // product.planIterations — resolved like effectiveStartDate / baseDiscountedFromPrice
  // so a single payment plan's "Ends after N payments" reads from the selected price.
  // TODO(M14): legacy product-scalar fallback — remove once the prices[] backfill migration has run (product.planIterations)
  const effectivePlanIterations =
    product.prices?.length > 0
      ? getPriceBillingConfig(selectedPrice)?.planIterations
      : product.planIterations;
  const planIterations = getPlanIterations({
    productPlanIterations: effectivePlanIterations,
    allowDynamicPlanIterations: checkout.allowDynamicPlanIterations,
    dynamicPlanIterations,
  });

  // The legacy unprefixed `discounted_from` carries no reference, so it is
  // single-price only — on a multi-price product it would restrike every option.
  const dynamicDiscountedFromPrice = queryDiscountedFrom(selectedPrice?.reference, query, {
    allowLegacyFallback: !isMultiPrice(product?.prices, query),
  });

  // TODO(M14): legacy product-scalar fallback — remove once the prices[] backfill migration has run (product.discountedFromPrice)
  const baseDiscountedFromPrice =
    product.prices?.length > 0 ? selectedPrice?.discountedFromPrice : product.discountedFromPrice;

  let discountedFromPrice =
    checkout.allowDynamicDiscountedFromPrice && dynamicDiscountedFromPrice >= 0
      ? dynamicDiscountedFromPrice
      : baseDiscountedFromPrice;

  // if bulk discount makes the price free, we still want to show the price
  // if the price was 0 to begin with, we don't want to show it

  // one-time charges — prices-first: the SELECTED price's cadence decides, with
  // the legacy product.type fallback living inside isRecurringPurchase.
  // TODO(M14): remove the legacy fallback after the prices[] backfill has run.
  if (!isRecurringPurchase(selectedPrice, product)) {
    const shouldShowPrice =
      amount > 0 || (amount === 0 && (discountedPrice > 0 || discountedFromPrice > 0));

    if (!shouldShowPrice) return '';

    return (
      <PriceWrapper>
        {/* A recurring price with a trial shows "N days free" + a "then $X /
            interval" price line, matching the subscription branch / multi-price
            PricePicker. The trial lives on the selected price in the multi-price
            model, so a charge product carrying a recurring price renders it here. */}
        {hasTrial && (
          <StyledElement
            elementPath="product.pricing.trialPeriod"
            className={clsx(
              'cp_product-price',
              'cp_product-price-trial-period',
              'bg-[var(--cp_product-price-trialPeriod-background-color)]',
              'text-[var(--cp_product-price-trialPeriod-color)]',
              '[font-family:var(--cp_product-price-trialPeriod-font-family)]',
              '[font-style:var(--cp_product-price-trialPeriod-font-style)]',
              '[font-size:var(--cp_product-price-trialPeriod-font-size)]',
              '[line-height:var(--cp_product-price-trialPeriod-line-height)]',
              '[font-weight:var(--cp_product-price-trialPeriod-font-weight)]',
            )}
          >
            <span className="cp_product-price-trial-period-days-text">
              {effectiveTrialPeriodDays}{' '}
            </span>
            <span className="cp_product-price-trial-period-days-free-text">
              {labels.trialDaysFreeText}{' '}
            </span>
          </StyledElement>
        )}
        <StyledElement
          elementPath="product.pricing.price"
          className={clsx(
            'cp_product-price',
            'bg-[var(--cp_product-price-background-color)]',
            'text-[var(--cp_product-price-color)]',
            '[font-family:var(--cp_product-price-font-family)]',
            '[font-style:var(--cp_product-price-font-style)]',
            '[font-size:var(--cp_product-price-font-size)]',
            '[line-height:var(--cp_product-price-line-height)]',
            '[font-weight:var(--cp_product-price-font-weight)]',
          )}
        >
          {hasTrial && (
            <span className="cp_product-price-trial-then-text">{labels.trialThenText} </span>
          )}
          <DiscountedFromPrice
            product={product}
            checkout={checkout}
            priceBeforeBulkDiscount={bulkDiscountAmount > 0 ? amount : null}
          />
          {shouldShowPrice && (
            <>
              <span className="cp_product-price-amount">
                {formatPrice({
                  amount: discountedPrice,
                  // TODO(M14): legacy product.currency — repoint to selected price before legacy fields are removed
                  currency: product.currency,
                  locale: currencyLocale,
                  style: 'currency',
                })}
              </span>

              {effectiveInterval && (
                <StyledElement
                  as="span"
                  elementPath="product.pricing.interval"
                  className={clsx(productPriceIntervalClasses)}
                >
                  {' '}
                  {getSubscriptionIntervalWithSlash(
                    effectiveInterval.interval,
                    effectiveInterval.intervalCount,
                    labels,
                  )}
                </StyledElement>
              )}

              <span className="cp_product-price-plus-sign">
                {/* TODO(M14): legacy product-scalar fallback — remove once the prices[] backfill migration has run (product.payWhatYouWant) */}
                {(selectedPrice ? selectedPrice.payWhatYouWant : product.payWhatYouWant) ? '+' : ''}
              </span>
            </>
          )}
        </StyledElement>

        {/* A charge product can carry a recurring price (e.g. a payment plan) in the
            multi-price model, so the start date + "Ends after N payments" lines must
            render here too — matching the subscription branch / production. */}
        {checkHasValidStartDate(effectiveStartDate) ? (
          <StyledElement
            elementPath="product.pricing.startDate"
            className={clsx(
              'cp_product-price-starts-on-text',
              'bg-[var(--cp_product-price-startDate-background-color)]',
              'text-[var(--cp_product-price-startDate-color)]',
              '[font-family:var(--cp_product-price-startDate-font-family)]',
              '[font-style:var(--cp_product-price-startDate-font-style)]',
              '[font-size:var(--cp_product-price-startDate-font-size)]',
              '[line-height:var(--cp_product-price-startDate-line-height)]',
              '[font-weight:var(--cp_product-price-startDate-font-weight)]',
            )}
          >
            <span className="cp_product-price-starts-on-text">{labels.startsOnText} </span>
            <span className="cp_product-price-starts-on-date">
              {formatStartDate(effectiveStartDate)}
            </span>
          </StyledElement>
        ) : (
          ''
        )}

        {planIterations ? (
          <div className="cp_product-price-ends-after-text">
            <StyledElement
              elementPath="product.pricing.planIterations"
              as="span"
              className={clsx(
                'cp_product-price-ends-after-text',
                'bg-[var(--cp_product-price-planIterations-background-color)]',
                'text-[var(--cp_product-price-planIterations-color)]',
                '[font-family:var(--cp_product-price-planIterations-font-family)]',
                '[font-style:var(--cp_product-price-planIterations-font-style)]',
                '[font-size:var(--cp_product-price-planIterations-font-size)]',
                '[line-height:var(--cp_product-price-planIterations-line-height)]',
                '[font-weight:var(--cp_product-price-planIterations-font-weight)]',
              )}
            >
              <span className="cp_product-price-ends-after-text">{labels.endsAfterText} </span>
              <span className="cp_product-price-ends-after-iterations">{planIterations} </span>
              <span className="cp_product-price-ends-after-payments-text">
                {labels.endsAfterPaymentsText}
              </span>
            </StyledElement>
          </div>
        ) : (
          ''
        )}

        {/* A charge-typed product can carry a recurring price (multi-price
            model), so it shows the billing-cycle-anchor proration too — the same
            "Renews on …" / "Today …" lines as the subscription branches, in the
            same order (after the price/trial/start-date/ends-after lines). Gated
            on hasProration, which is false for a true one-time charge. */}
        {hasProration && !isLoadingProration && (
          <div className="cp_product-price-proration-wrapper">
            <StyledElement
              elementPath="product.pricing.prorationRenews"
              className={clsx(
                'cp_product-price-proration-renews-wrapper',
                'bg-[var(--cp_product-price-prorationRenews-background-color)]',
                'text-[var(--cp_product-price-prorationRenews-color)]',
                '[font-family:var(--cp_product-price-prorationRenews-font-family)]',
                '[font-style:var(--cp_product-price-prorationRenews-font-style)]',
                '[font-size:var(--cp_product-price-prorationRenews-font-size)]',
                '[line-height:var(--cp_product-price-prorationRenews-line-height)]',
                '[font-weight:var(--cp_product-price-prorationRenews-font-weight)]',
              )}
            >
              <span className="cp_product-price-proration-date-text">{labels.renewsOnText} </span>
              {proration.nextBillingDate && (
                <span className="cp_product-price-proration-date">
                  {formatInTimeZone(new Date(proration.nextBillingDate), 'UTC', 'd MMM, yyyy')}
                </span>
              )}
            </StyledElement>

            <StyledElement
              elementPath="product.pricing.prorationAmount"
              className={clsx(
                'cp_product-price-proration-amount-wrapper',
                'bg-[var(--cp_product-price-prorationAmount-background-color)]',
                'text-[var(--cp_product-price-prorationAmount-color)]',
                '[font-family:var(--cp_product-price-prorationAmount-font-family)]',
                '[font-style:var(--cp_product-price-prorationAmount-font-style)]',
                '[font-size:var(--cp_product-price-prorationAmount-font-size)]',
                '[line-height:var(--cp_product-price-prorationAmount-line-height)]',
                '[font-weight:var(--cp_product-price-prorationAmount-font-weight)]',
              )}
            >
              <span className="cp_product-price-proration-amount-today-text">
                {labels.todayText}{' '}
              </span>
              <span className="cp_product-price-proration-amount">
                {formatPrice({
                  amount: proration.amount,
                  // TODO(M14): legacy product.currency — repoint to selected price before legacy fields are removed
                  currency: product.currency,
                  locale: currencyLocale,
                  style: 'currency',
                })}{' '}
              </span>
              {!proration.fullPrice && (
                <span className="cp_product-price-proration-amount-text">
                  {labels.proratedAmountText}
                </span>
              )}
            </StyledElement>
          </div>
        )}
      </PriceWrapper>
    );
  }

  // subscriptions with trial period
  if (effectiveTrialPeriodDays) {
    return (
      <PriceWrapper>
        <div className="cp_product-price">
          <StyledElement
            elementPath="product.pricing.trialPeriod"
            className={clsx(
              'cp_product-price-trial-period',
              'bg-[var(--cp_product-price-trialPeriod-background-color)]',
              'text-[var(--cp_product-price-trialPeriod-color)]',
              '[font-family:var(--cp_product-price-trialPeriod-font-family)]',
              '[font-style:var(--cp_product-price-trialPeriod-font-style)]',
              '[font-size:var(--cp_product-price-trialPeriod-font-size)]',
              '[line-height:var(--cp_product-price-trialPeriod-line-height)]',
              '[font-weight:var(--cp_product-price-trialPeriod-font-weight)]',
            )}
          >
            <span className="cp_product-price-trial-period-days-text">
              {effectiveTrialPeriodDays}{' '}
            </span>
            <span className="cp_product-price-trial-period-days-free-text">
              {labels.trialDaysFreeText}{' '}
            </span>
          </StyledElement>

          {(amount > 0 || discountedFromPrice > 0) && (
            <>
              <StyledElement
                elementPath="product.pricing.price"
                className={clsx(
                  'cp_product-price',
                  'bg-[var(--cp_product-price-background-color)]',
                  'text-[var(--cp_product-price-color)]',
                  '[font-family:var(--cp_product-price-font-family)]',
                  '[font-style:var(--cp_product-price-font-style)]',
                  '[font-size:var(--cp_product-price-font-size)]',
                  '[line-height:var(--cp_product-price-line-height)]',
                  '[font-weight:var(--cp_product-price-font-weight)]',
                )}
              >
                <span className="cp_product-price-trial-then-text">{labels.trialThenText} </span>
                <DiscountedFromPrice product={product} checkout={checkout} />
                <span className="cp_product-price-trial-then-amount">
                  {formatPrice({
                    amount,
                    // TODO(M14): legacy product.currency — repoint to selected price before legacy fields are removed
                    currency: product.currency,
                    locale: currencyLocale,
                    style: 'currency',
                  })}{' '}
                </span>
                <StyledElement
                  as="span"
                  elementPath="product.pricing.interval"
                  className={clsx(
                    productPriceIntervalClasses,
                    'cp_product-price-trial-then-interval',
                  )}
                >
                  {getSubscriptionIntervalWithSlash(
                    effectiveInterval?.interval,
                    effectiveInterval?.intervalCount,
                    labels,
                  )}
                </StyledElement>
              </StyledElement>
            </>
          )}

          {checkHasValidStartDate(effectiveStartDate) ? (
            <StyledElement
              elementPath="product.pricing.startDate"
              className={clsx(
                'cp_product-price-starts-on-text',
                'bg-[var(--cp_product-price-startDate-background-color)]',
                'text-[var(--cp_product-price-startDate-color)]',
                '[font-family:var(--cp_product-price-startDate-font-family)]',
                '[font-style:var(--cp_product-price-startDate-font-style)]',
                '[font-size:var(--cp_product-price-startDate-font-size)]',
                '[line-height:var(--cp_product-price-startDate-line-height)]',
                '[font-weight:var(--cp_product-price-startDate-font-weight)]',
              )}
            >
              <span className="cp_product-price-starts-on-text">{labels.startsOnText} </span>
              <span className="cp_product-price-starts-on-date">
                {formatStartDate(effectiveStartDate)}
              </span>
            </StyledElement>
          ) : (
            ''
          )}

          {planIterations ? (
            <div className="cp_product-price-ends-after-text">
              <StyledElement
                elementPath="product.pricing.planIterations"
                as="span"
                className={clsx(
                  'cp_product-price-ends-after-text',
                  'bg-[var(--cp_product-price-planIterations-background-color)]',
                  'text-[var(--cp_product-price-planIterations-color)]',
                  '[font-family:var(--cp_product-price-planIterations-font-family)]',
                  '[font-style:var(--cp_product-price-planIterations-font-style)]',
                  '[font-size:var(--cp_product-price-planIterations-font-size)]',
                  '[line-height:var(--cp_product-price-planIterations-line-height)]',
                  '[font-weight:var(--cp_product-price-planIterations-font-weight)]',
                )}
              >
                <span className="cp_product-price-ends-after-text">{labels.endsAfterText} </span>
                <span className="cp_product-price-ends-after-iterations">{planIterations} </span>
                <span className="cp_product-price-ends-after-payments-text">
                  {labels.endsAfterPaymentsText}
                </span>
              </StyledElement>
            </div>
          ) : (
            ''
          )}

          {hasProration && !isLoadingProration && (
            <div className="cp_product-price-proration-wrapper">
              <StyledElement
                elementPath="product.pricing.prorationRenews"
                className={clsx(
                  'cp_product-price-proration-renews-wrapper',
                  'bg-[var(--cp_product-price-prorationRenews-background-color)]',
                  'text-[var(--cp_product-price-prorationRenews-color)]',
                  '[font-family:var(--cp_product-price-prorationRenews-font-family)]',
                  '[font-style:var(--cp_product-price-prorationRenews-font-style)]',
                  '[font-size:var(--cp_product-price-prorationRenews-font-size)]',
                  '[line-height:var(--cp_product-price-prorationRenews-line-height)]',
                  '[font-weight:var(--cp_product-price-prorationRenews-font-weight)]',
                )}
              >
                <span className="cp_product-price-proration-date-text">{labels.renewsOnText} </span>
                {proration.nextBillingDate && (
                  <span className="cp_product-price-proration-date">
                    {formatInTimeZone(new Date(proration.nextBillingDate), 'UTC', 'd MMM, yyyy')}
                  </span>
                )}
              </StyledElement>

              <StyledElement
                elementPath="product.pricing.prorationAmount"
                className={clsx(
                  'cp_product-price-proration-amount-wrapper',
                  'bg-[var(--cp_product-price-prorationAmount-background-color)]',
                  'text-[var(--cp_product-price-prorationAmount-color)]',
                  '[font-family:var(--cp_product-price-prorationAmount-font-family)]',
                  '[font-style:var(--cp_product-price-prorationAmount-font-style)]',
                  '[font-size:var(--cp_product-price-prorationAmount-font-size)]',
                  '[line-height:var(--cp_product-price-prorationAmount-line-height)]',
                  '[font-weight:var(--cp_product-price-prorationAmount-font-weight)]',
                )}
              >
                <span className="cp_product-price-proration-amount-today-text">
                  {labels.todayText}{' '}
                </span>
                <span className="cp_product-price-proration-amount">
                  {formatPrice({
                    amount: proration.amount,
                    // TODO(M14): legacy product.currency — repoint to selected price before legacy fields are removed
                    currency: product.currency,
                    locale: currencyLocale,
                    style: 'currency',
                  })}{' '}
                </span>
                {!proration.fullPrice && (
                  <span className="cp_product-price-proration-amount-text">
                    {labels.proratedAmountText}
                  </span>
                )}
              </StyledElement>
            </div>
          )}
        </div>
      </PriceWrapper>
    );
  }

  // subscriptions with start date
  if (checkHasValidStartDate(effectiveStartDate)) {
    return (
      <PriceWrapper>
        <StyledElement
          elementPath="product.pricing.price"
          className={clsx(
            'cp_product-price',
            'bg-[var(--cp_product-price-background-color)]',
            'text-[var(--cp_product-price-color)]',
            '[font-family:var(--cp_product-price-font-family)]',
            '[font-style:var(--cp_product-price-font-style)]',
            '[font-size:var(--cp_product-price-font-size)]',
            '[line-height:var(--cp_product-price-line-height)]',
            '[font-weight:var(--cp_product-price-font-weight)]',
          )}
        >
          <DiscountedFromPrice product={product} checkout={checkout} />

          {(amount > 0 || discountedFromPrice > 0) && (
            <>
              <span className="cp_product-price-amount">
                {formatPrice({
                  amount,
                  // TODO(M14): legacy product.currency — repoint to selected price before legacy fields are removed
                  currency: product.currency,
                  locale: currencyLocale,
                  style: 'currency',
                })}{' '}
              </span>
              <StyledElement
                as="span"
                elementPath="product.pricing.interval"
                className={clsx(productPriceIntervalClasses)}
              >
                {getSubscriptionIntervalWithSlash(
                  effectiveInterval?.interval,
                  effectiveInterval?.intervalCount,
                  labels,
                )}
              </StyledElement>
            </>
          )}
        </StyledElement>

        <StyledElement
          elementPath="product.pricing.startDate"
          className={clsx(
            'cp_product-price-starts-on-wrapper',
            'bg-[var(--cp_product-price-startDate-background-color)]',
            'text-[var(--cp_product-price-startDate-color)]',
            '[font-family:var(--cp_product-price-startDate-font-family)]',
            '[font-style:var(--cp_product-price-startDate-font-style)]',
            '[font-size:var(--cp_product-price-startDate-font-size)]',
            '[line-height:var(--cp_product-price-startDate-line-height)]',
            '[font-weight:var(--cp_product-price-startDate-font-weight)]',
          )}
        >
          <span className="cp_product-price-starts-on-text">{labels.startsOnText} </span>
          <span className="cp_product-price-starts-on-date">
            {formatStartDate(effectiveStartDate)}
          </span>
        </StyledElement>

        {amount > 0 && planIterations ? (
          <div className="cp_product-price-ends-after-wrapper">
            <StyledElement
              elementPath="product.pricing.planIterations"
              as="span"
              className={clsx(
                'cp_product-price-ends-after-text',
                'bg-[var(--cp_product-price-planIterations-background-color)]',
                'text-[var(--cp_product-price-planIterations-color)]',
                '[font-family:var(--cp_product-price-planIterations-font-family)]',
                '[font-style:var(--cp_product-price-planIterations-font-style)]',
                '[font-size:var(--cp_product-price-planIterations-font-size)]',
                '[line-height:var(--cp_product-price-planIterations-line-height)]',
                '[font-weight:var(--cp_product-price-planIterations-font-weight)]',
              )}
            >
              <span className="cp_product-price-ends-after-text">{labels.endsAfterText} </span>
              <span className="cp_product-price-ends-after-iterations">{planIterations} </span>
              <span className="cp_product-price-ends-after-payments-text">
                {labels.endsAfterPaymentsText}
              </span>
            </StyledElement>
          </div>
        ) : (
          ''
        )}

        {hasProration && !isLoadingProration && (
          <div className="cp_product-price-proration-wrapper">
            <StyledElement
              elementPath="product.pricing.prorationRenews"
              className={clsx(
                'cp_product-price-proration-renews-wrapper',
                'bg-[var(--cp_product-price-prorationRenews-background-color)]',
                'text-[var(--cp_product-price-prorationRenews-color)]',
                '[font-family:var(--cp_product-price-prorationRenews-font-family)]',
                '[font-style:var(--cp_product-price-prorationRenews-font-style)]',
                '[font-size:var(--cp_product-price-prorationRenews-font-size)]',
                '[line-height:var(--cp_product-price-prorationRenews-line-height)]',
                '[font-weight:var(--cp_product-price-prorationRenews-font-weight)]',
              )}
            >
              <span className="cp_product-price-proration-date-text">{labels.renewsOnText} </span>
              {proration.nextBillingDate && (
                <span className="cp_product-price-proration-date">
                  {formatInTimeZone(new Date(proration.nextBillingDate), 'UTC', 'd MMM, yyyy')}
                </span>
              )}
            </StyledElement>

            <StyledElement
              elementPath="product.pricing.prorationAmount"
              className={clsx(
                'cp_product-price-proration-amount-wrapper',
                'bg-[var(--cp_product-price-prorationAmount-background-color)]',
                'text-[var(--cp_product-price-prorationAmount-color)]',
                '[font-family:var(--cp_product-price-prorationAmount-font-family)]',
                '[font-style:var(--cp_product-price-prorationAmount-font-style)]',
                '[font-size:var(--cp_product-price-prorationAmount-font-size)]',
                '[line-height:var(--cp_product-price-prorationAmount-line-height)]',
                '[font-weight:var(--cp_product-price-prorationAmount-font-weight)]',
              )}
            >
              <span className="cp_product-price-proration-amount-today-text">
                {labels.todayText}{' '}
              </span>
              <span className="cp_product-price-proration-amount">
                {formatPrice({
                  amount: proration.amount,
                  // TODO(M14): legacy product.currency — repoint to selected price before legacy fields are removed
                  currency: product.currency,
                  locale: currencyLocale,
                  style: 'currency',
                })}{' '}
              </span>
              {!proration.fullPrice && (
                <span className="cp_product-price-proration-amount-text">
                  {labels.proratedAmountText}
                </span>
              )}
            </StyledElement>
          </div>
        )}
      </PriceWrapper>
    );
  }

  const shouldShowPrice = amount > 0 || discountedFromPrice > 0;

  if (!shouldShowPrice) return '';

  return (
    <PriceWrapper>
      <StyledElement
        elementPath="product.pricing.price"
        className={clsx(
          'cp_product-price',
          'bg-[var(--cp_product-price-background-color)]',
          'text-[var(--cp_product-price-color)]',
          '[font-family:var(--cp_product-price-font-family)]',
          '[font-style:var(--cp_product-price-font-style)]',
          '[font-size:var(--cp_product-price-font-size)]',
          '[line-height:var(--cp_product-price-line-height)]',
          '[font-weight:var(--cp_product-price-font-weight)]',
        )}
      >
        <DiscountedFromPrice product={product} checkout={checkout} />

        {(amount > 0 || discountedFromPrice > 0) && (
          <>
            <span className="cp_product-price-amount">
              {formatPrice({
                amount,
                // TODO(M14): legacy product.currency — repoint to selected price before legacy fields are removed
                currency: product.currency,
                locale: currencyLocale,
                style: 'currency',
              })}{' '}
            </span>
            <StyledElement
              as="span"
              elementPath="product.pricing.interval"
              className={clsx(productPriceIntervalClasses)}
            >
              {getSubscriptionIntervalWithSlash(
                effectiveInterval?.interval,
                effectiveInterval?.intervalCount,
                labels,
              )}
            </StyledElement>
          </>
        )}
      </StyledElement>

      {checkHasValidStartDate(effectiveStartDate) ? (
        <StyledElement
          elementPath="product.pricing.startDate"
          className={clsx(
            'cp_product-price-starts-on-wrapper',
            'bg-[var(--cp_product-price-startDate-background-color)]',
            'text-[var(--cp_product-price-startDate-color)]',
            '[font-family:var(--cp_product-price-startDate-font-family)]',
            '[font-style:var(--cp_product-price-startDate-font-style)]',
            '[font-size:var(--cp_product-price-startDate-font-size)]',
            '[line-height:var(--cp_product-price-startDate-line-height)]',
            '[font-weight:var(--cp_product-price-startDate-font-weight)]',
          )}
        >
          <span className="cp_product-price-starts-on-text">{labels.startsOnText} </span>
          <span className="cp_product-price-starts-on-date">
            {formatStartDate(effectiveStartDate)}
          </span>
        </StyledElement>
      ) : (
        ''
      )}

      {amount > 0 && planIterations ? (
        <StyledElement
          elementPath="product.pricing.planIterations"
          className={clsx(
            'cp_product-price-ends-after-wrapper',
            'bg-[var(--cp_product-price-planIterations-background-color)]',
            'text-[var(--cp_product-price-planIterations-color)]',
            '[font-family:var(--cp_product-price-planIterations-font-family)]',
            '[font-style:var(--cp_product-price-planIterations-font-style)]',
            '[font-size:var(--cp_product-price-planIterations-font-size)]',
            '[line-height:var(--cp_product-price-planIterations-line-height)]',
            '[font-weight:var(--cp_product-price-planIterations-font-weight)]',
          )}
        >
          <span className="cp_product-price-ends-after-text">{labels.endsAfterText} </span>
          <span className="cp_product-price-ends-after-iterations">{planIterations} </span>
          <span className="cp_product-price-ends-after-payments-text">
            {labels.endsAfterPaymentsText}
          </span>
        </StyledElement>
      ) : (
        ''
      )}

      {hasProration && !isLoadingProration && (
        <div className="cp_product-price-proration-wrapper">
          <div className="cp_product-price-proration-date-wrapper">
            <StyledElement
              elementPath="product.pricing.prorationRenews"
              as="span"
              className={clsx(
                'cp_product-price-proration-renews-wrapper',
                'bg-[var(--cp_product-price-prorationRenews-background-color)]',
                'text-[var(--cp_product-price-prorationRenews-color)]',
                '[font-family:var(--cp_product-price-prorationRenews-font-family)]',
                '[font-style:var(--cp_product-price-prorationRenews-font-style)]',
                '[font-size:var(--cp_product-price-prorationRenews-font-size)]',
                '[line-height:var(--cp_product-price-prorationRenews-line-height)]',
                '[font-weight:var(--cp_product-price-prorationRenews-font-weight)]',
              )}
            >
              <span className="cp_product-price-proration-date-text">{labels.renewsOnText} </span>
              {proration.nextBillingDate && (
                <span className="cp_product-price-proration-date">
                  {formatInTimeZone(new Date(proration.nextBillingDate), 'UTC', 'd MMM, yyyy')}
                </span>
              )}
            </StyledElement>

            <StyledElement
              elementPath="product.pricing.prorationAmount"
              className={clsx(
                'cp_product-price-proration-amount-wrapper',
                'bg-[var(--cp_product-price-prorationAmount-background-color)]',
                'text-[var(--cp_product-price-prorationAmount-color)]',
                '[font-family:var(--cp_product-price-prorationAmount-font-family)]',
                '[font-style:var(--cp_product-price-prorationAmount-font-style)]',
                '[font-size:var(--cp_product-price-prorationAmount-font-size)]',
                '[line-height:var(--cp_product-price-prorationAmount-line-height)]',
                '[font-weight:var(--cp_product-price-prorationAmount-font-weight)]',
              )}
            >
              <span className="cp_product-price-proration-amount-today-text">
                {labels.todayText}{' '}
              </span>
              <span className="cp_product-price-proration-amount">
                {formatPrice({
                  amount: proration.amount,
                  // TODO(M14): legacy product.currency — repoint to selected price before legacy fields are removed
                  currency: product.currency,
                  locale: currencyLocale,
                  style: 'currency',
                })}{' '}
              </span>
              {!proration.fullPrice && (
                <span className="cp_product-price-proration-amount-text">
                  {labels.proratedAmountText}
                </span>
              )}
            </StyledElement>
          </div>
        </div>
      )}
    </PriceWrapper>
  );
}

// eslint-disable-next-line react/display-name
export const Logo = React.memo(({ width, height }) => {
  const { seller, isPreview } = useCheckoutState();
  const logo = getImageSrcAndSrcSet({ url: seller.logo?.url, width, height, isPreview });

  if (!logo) {
    return (
      <Avatar.Root className="cp_product-logo-fallback-wrapper">
        <Avatar.Fallback
          className={clsx(
            'cp_product-logo-fallback w-full h-full flex rounded-full uppercase items-center justify-center overflow-hidden font-bold leading-loose',
            'bg-[var(--cp_store-logo-background-color)] text-[var(--cp_store-logo-text-color)]',
          )}
          style={{
            width: `${width}px`,
            height: `${height}px`,
            fontSize: `${width * 0.5}px`,
          }}
        >
          {seller.displayName?.charAt(0)}
        </Avatar.Fallback>
      </Avatar.Root>
    );
  }

  return (
    <img
      src={logo.src}
      srcSet={logo.srcSet}
      alt={seller.name}
      className="cp_product-logo rounded-full"
      width={width}
      height={height}
      loading="eager"
    />
  );
});
